/**
 * @file Sidebar.jsx
 * @description Hauptnavigationsleiste (Seitenleiste / Drawer) von ThekenOS.
 *
 * Wie die Menüsteuerung funktioniert:
 * - Normale Thekenkräfte sehen: Dashboard, Daily Todos, Thekipedia (Wiki) und Gesprächsnotizen.
 * - Einkäufer sehen: Direkt und ausschließlich die Einkaufsliste.
 * - Redakteure & Admins sehen zusätzlich: Aufgaben verwalten (Vorlagen) und Feedback & Bugs.
 * - System-Admins & Admins sehen zusätzlich: Benutzer- & Geräteverwaltung.
 * - Enthält den zentralen Abmelde-Button (`Abmelden`).
 */

import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canAccessEinkauf } from '../config/security';
import { Home, CheckSquare, Book, FileText, LogOut, X, Settings, Shield, MessageSquare, ShoppingCart, Sliders } from 'lucide-react';
import './Sidebar.css';

/**
 * Seitenleisten-Komponente für die App-Navigation.
 *
 * @param {object} props - Komponenten-Properties
 * @param {boolean} props.isOpen - Ob die Leiste sichtbar/ausgeklappt ist
 * @param {Function} props.onClose - Funktion zum Schließen der Leiste (z. B. nach Klick auf einen Link)
 * @returns {JSX.Element} Gerendertes Navigationsmenü
 */
export default function Sidebar({ isOpen, onClose }) {
  const { logout, role, permissions } = useAuth();
  const isEinkaufer = role === 'Einkäufer';
  // Dieselbe Regel, die auch der Routen-Wächter für /einkauf anwendet (siehe config/security.js)
  const showEinkauf = canAccessEinkauf(role, permissions);

  /**
   * Meldet den Benutzer ab und schließt das Menü.
   */
  const handleLogout = async () => {
    await logout();
    onClose();
  };

  return (
    <>
      {/* Dunkler Hintergrund (Backdrop), der die Leiste bei Klick außerhalb schließt */}
      <div
        className={`sidebar-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>ThekeOS</h2>
          <button className="close-btn" onClick={onClose} aria-label="Menü schließen">
            <X size={24} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {/* Reguläre Menüpunkte für Thekenkräfte (reine Einkäufer sehen diese nicht) */}
          {!isEinkaufer && (
            <>
              <NavLink to="/dashboard" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} onClick={onClose}>
                <Home size={20} />
                <span>Dashboard</span>
              </NavLink>

              <NavLink to="/todos" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} onClick={onClose}>
                <CheckSquare size={20} />
                <span>Daily Todos</span>
              </NavLink>

              <NavLink to="/thekipedia" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} onClick={onClose}>
                <Book size={20} />
                <span>Thekipedia</span>
              </NavLink>

              <NavLink to="/notes" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} onClick={onClose}>
                <FileText size={20} />
                <span>Gesprächsnotizen</span>
              </NavLink>
            </>
          )}

          {/* Menüpunkt Einkaufsliste für berechtigte Rollen */}
          {showEinkauf && (
            <NavLink to="/einkauf" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} onClick={onClose}>
              <ShoppingCart size={20} />
              <span>Einkaufsliste</span>
            </NavLink>
          )}

          {/* Erweiterte Funktionen für Redakteure & Administratoren */}
          {!isEinkaufer && (
            <>
              {['Redakteur', 'Admin', 'System-Admin'].includes(role) && (
                <NavLink to="/admin/templates" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} onClick={onClose}>
                  <Settings size={20} />
                  <span>Aufgaben verwalten</span>
                </NavLink>
              )}

              {['Redakteur', 'Admin', 'System-Admin', 'Beta-Theki'].includes(role) && (
                <NavLink to="/feedback" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} onClick={onClose}>
                  <MessageSquare size={20} />
                  <span>Feedback & Bugs</span>
                </NavLink>
              )}
              
              {/* Zentraler Administrationsbereich für Admin & System-Admin: Alle Admin-Tools über die System-Einstellungen */}
              {['Admin', 'System-Admin'].includes(role) && (
                <NavLink to="/admin/settings" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} onClick={onClose}>
                  <Sliders size={20} />
                  <span>System-Einstellungen</span>
                </NavLink>
              )}
            </>
          )}

        </nav>

        <div className="sidebar-footer">
          {['Admin', 'System-Admin'].includes(role) && (
            <div className="admin-badge">{role} Mode</div>
          )}
          <button className="nav-item logout-btn" onClick={handleLogout}>
            <LogOut size={20} />
            <span>Abmelden</span>
          </button>
        </div>
      </aside>
    </>
  );
}
